import React, { useId } from "react";

/* ============================================================
   DNH — Shared editorial atoms used across pages.
   ============================================================ */

export const ConfidenceBadge = ({
  level = "High",
  sources = null,
  className = "",
}) => {
  const uid = useId().replace(/:/g, "");
  const map = {
    High: { dot: "●", label: "High", color: "var(--crimson)" },
    Moderate: { dot: "◐", label: "Moderate", color: "var(--ink)" },
    Developing: { dot: "○", label: "Developing", color: "var(--muted)" },
  };
  const m = map[level] || map.Developing;

  // sources can be a number (source_count from API) or a string (source_name fallback)
  // When count is 1, we intentionally show nothing here — the parent shows "via Publication" instead
  const sourcesLabel =
    typeof sources === "number" && sources > 1
      ? `${sources} sources`
      : typeof sources === "string" && sources
      ? sources
      : null;

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono text-[11px] leading-none text-[var(--muted)] overflow-hidden ${className}`}
      data-testid={`confidence-badge-${uid}`}
      title={typeof sources === "number" ? "Distinct publishers with linked coverage of this event" : undefined}
    >
      <span style={{ color: m.color }} className="text-[12px] leading-none shrink-0">
        {m.dot}
      </span>
      <span className="uppercase tracking-[0.08em] text-[var(--ink-2)] truncate">
        {m.label}
        {sourcesLabel && (
          <span className="text-[var(--muted)] normal-case tracking-normal font-medium"> · {sourcesLabel}</span>
        )}
      </span>
    </span>
  );
};

export const SectionLabel = ({ children }) => (
  <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-[var(--muted)]">
    {children}
  </div>
);

export const SectionHeading = ({ label, title, subtitle, action }) => (
  <div className="flex items-end justify-between gap-6 mb-8 sm:mb-10">
    <div className="max-w-2xl">
      {label && <SectionLabel>{label}</SectionLabel>}
      <h2 className="font-serif text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.05] tracking-[-0.01em] text-[var(--ink)] mt-2">
        {title}
      </h2>
      {subtitle && (
        <p className="font-sans text-[15px] sm:text-base text-[var(--muted)] mt-3 leading-[1.6]">
          {subtitle}
        </p>
      )}
    </div>
    {action && <div className="hidden sm:block">{action}</div>}
  </div>
);
